import React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { humanizeLineItemCode } from '../../util/data';
import {
  formatPetType,
  formatPricingType,
  formatPetCount,
  formatDuration
} from '../../util/petHelper';

import {
  ToolTip,
} from '../../components';

import css from './OrderBreakdown.module.css';

/**
 * A component that renders pet pricing line items with detailed breakdown.
 * Shows the format: "Base puppies standard × 1 puppy × 2 days"
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {boolean} props.isProvider - Whether the provider is the one viewing the breakdown
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemPetPricingMaybe = props => {
  const { lineItems, isProvider, intl } = props;

  // console.log('LineItemPetPricingMaybe lineItems', lineItems);

  // Filter for pet pricing line items
  const petLineItems = lineItems.filter(item => {
    const isPetItem = item.code.includes('base-') || item.code.includes('additional-');
    const isPetPricing = item.code.includes('-dogs') || item.code.includes('-puppies') || item.code.includes('-cats');
    return isPetItem && isPetPricing && !item.reversal;
  });

  // Filter by user role
  const items = isProvider
    ? petLineItems.filter(item => item.includeFor.includes('provider'))
    : petLineItems.filter(item => item.includeFor.includes('customer'));

  if (items.length === 0) {
    return null;
  }

  return (
    <React.Fragment>
      {items.map((item, i) => {
        const petType = formatPetType(item.code);
        const pricingType = formatPricingType(item.code);
        const petCount = item.petCount || 1;
        const days = item.days || parseInt(item?.quantity ? item.quantity.toString() : 0, 10) || 0;

        // Create the detailed label
        let label = '';
        let day_text = formatDuration(days);
        if (item.code.includes('base-')) {
          label = `${pricingType} ${formatPetCount(1, petType)}${day_text}`;
        } else if (item.code.includes('additional-')) {
          label = `Additional ${pricingType} ${formatPetCount(petCount, petType)}${day_text}`;
        } else {
          label = humanizeLineItemCode(item.code);
        }

        const formattedTotal = formatMoney(intl, item.lineTotal);

        const popover = (
          <ToolTip
            tip={(<span>{`Price per Day entered in Listing.`}</span>)}
            isSuperScript
          />
        );
        
        return (
          <div key={`${i}-${item.code}`} className={css.lineItem}>
            <span className={css.itemLabel}>{label}</span>
            <span className={css.itemValue}>{formattedTotal}{popover}</span>
          </div>
        );
      })}
    </React.Fragment>
  );
};

export default LineItemPetPricingMaybe; 